import * as React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import { Modal } from "@mui/material";
import toast from "react-hot-toast";

import "./PropertyPostSystemMenuIcon.scss";
import SubMenuThreeDotIcon from "../../../../../components/icons/SubMenuThreeDotIcon";
import { PropertyApi } from "../../../../../utils/api/Property";

type StatusAction = "cancel" | "restore" | "archive" | "unarchive";

const PropertyPostSystemMenuIcon = ({
  postId,
  currentStatus,
  onPostNow,
  onSchedulePost,
  onUpdated,
  disabled,
}: {
  postId: string;
  currentStatus?: string;
  onPostNow?: () => void;
  onSchedulePost?: () => void;
  onUpdated?: () => void;
  disabled?: boolean;
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [processingOpen, setProcessingOpen] = React.useState(false);

  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [restoreOpen, setRestoreOpen] = React.useState(false);
  const [archiveOpen, setArchiveOpen] = React.useState(false);
  const [unarchiveOpen, setUnarchiveOpen] = React.useState(false);

  const open = Boolean(anchorEl);

  const isCancelled = currentStatus === "cancelled";
  const isArchived = currentStatus === "archived";

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const closeAllConfirmations = () => {
    setCancelOpen(false);
    setRestoreOpen(false);
    setArchiveOpen(false);
    setUnarchiveOpen(false);
  };

  const handleStatusUpdate = async (type: StatusAction) => {
    try {
      setProcessingOpen(true);
      setIsLoading(true);

      let updateData: any = {};

      switch (type) {
        case "cancel":
          updateData = { post_status: "cancelled", schedule_datetime: null };
          break;

        case "restore":
          updateData = { post_status: "schedule_for_upload" };
          break;

        case "archive":
          updateData = { post_status: "archived", schedule_datetime: null };
          break;

        case "unarchive":
          updateData = { post_status: "schedule_for_upload" };
          break;
      }

      const res = await PropertyApi.updatePropertyPost(postId, updateData);

      if (res?.data?.success) {
        toast.success("Post updated successfully");
        onUpdated?.();
      } else {
        toast.error(res?.data?.message || "Failed to update post");
      }
    } catch (error) {
      console.error("Failed to update post:", error);
      toast.error("Failed to update post");
    } finally {
      setIsLoading(false);
      setProcessingOpen(false);
      closeAllConfirmations();
      handleClose();
    }
  };

  return (
    <>
      <div
        className="property-post-all-menu-icon"
        onClick={handleClick}
        id="post-system-menu-btn"
        aria-controls={open ? "post-system-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        style={{
          pointerEvents: disabled ? "none" : "auto",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {isLoading ? (
          <CircularProgress
            className="circular-progress-action-icon"
            style={{ width: 15, height: 15 }}
          />
        ) : (
          <SubMenuThreeDotIcon />
        )}
      </div>

      <Menu
        id="post-system-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ "aria-labelledby": "post-system-menu-btn" }}
        className="custom-action-menu"
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: "10px",
            mt: 1,
            width: 220,
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            onPostNow?.();
          }}
        >
          Post now
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleClose();
            onSchedulePost?.();
          }}
        >
          Schedule post
        </MenuItem>

        {!isCancelled ? (
          <MenuItem
            onClick={() => {
              handleClose();
              setCancelOpen(true);
            }}
          >
            Cancel
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              handleClose();
              setRestoreOpen(true);
            }}
          >
            Restore
          </MenuItem>
        )}

        {!isArchived ? (
          <MenuItem
            onClick={() => {
              handleClose();
              setArchiveOpen(true);
            }}
          >
            Archive
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              handleClose();
              setUnarchiveOpen(true);
            }}
          >
            Unarchive
          </MenuItem>
        )}
      </Menu>

      <Modal
        open={processingOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") setProcessingOpen(false);
        }}
        aria-labelledby="processing-modal"
        className="add-property-modal styled-modal"
        disableEscapeKeyDown
      >
        <div className="modal-center-wrapper">
          <div className="loading-content-wrapper">
            <div className="loading-container">
              <div className="spinner" />
              <p className="loading-text">
                Processing your request
                <span className="desktop-break">
                  <br />
                </span>
                Please wait...
              </p>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        className="ppsmi-cancel-modal"
      >
        <div className="ppsmi-cancel-card">
          <h2 className="ppsmi-cancel-title">Cancel property post?</h2>

          <p className="ppsmi-cancel-desc">
            Are you sure you want to cancel this post?
            {"\n\n"}
            It will not be published as scheduled, but you can restore or edit
            it later from the Property posts list
          </p>

          <div className="ppsmi-cancel-actions">
            <button
              className="ppsmi-cancel-btn"
              onClick={() => setCancelOpen(false)}
              type="button"
            >
              Cancel
            </button>

            <button
              className="ppsmi-confirm-btn"
              onClick={() => handleStatusUpdate("cancel")}
              type="button"
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
        className="ppsmi-restore-modal"
      >
        <div className="ppsmi-restore-card">
          <h2 className="ppsmi-restore-title">Restore property post?</h2>

          <p className="ppsmi-restore-desc">
            Are you sure you want to restore this post?
            {"\n\n"}
            It will return to the active list and publish automatically if the
            scheduled time is still in the future.
            {"\n\n"}
            If the scheduled time has passed, you can set a new time or publish
            it immediately using the Post Now feature.
          </p>

          <div className="ppsmi-restore-actions">
            <button
              className="ppsmi-restore-cancel-btn"
              onClick={() => setRestoreOpen(false)}
              type="button"
            >
              Cancel
            </button>

            <button
              className="ppsmi-restore-confirm-btn"
              onClick={() => handleStatusUpdate("restore")}
              type="button"
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        className="ppsmi-archive-modal"
      >
        <div className="ppsmi-archive-card">
          <h2 className="ppsmi-archive-title">Archive this post?</h2>

          <p className="ppsmi-archive-desc">
            This will move the post to your archives. It will no longer appear
            in your active list but can be restored at any time.
            {"\n\n"}
            <span className="ppsmi-archive-note-title">Please note:</span>
            {"\n"}
            Archived posts will not be published to social media.
          </p>

          <div className="ppsmi-archive-actions">
            <button
              className="ppsmi-archive-cancel-btn"
              onClick={() => setArchiveOpen(false)}
              type="button"
            >
              Cancel
            </button>

            <button
              className="ppsmi-archive-confirm-btn"
              onClick={() => handleStatusUpdate("archive")}
              type="button"
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={unarchiveOpen}
        onClose={() => setUnarchiveOpen(false)}
        className="ppsmi-unarchive-modal"
      >
        <div className="ppsmi-unarchive-card">
          <h2 className="ppsmi-unarchive-title">Unarchive this post?</h2>

          <p className="ppsmi-unarchive-desc">
            It will return to the active list and publish automatically if the
            scheduled time is still in the future.
            {"\n\n"}
            If the scheduled time has passed, you can set a new time or publish
            it immediately using the Post Now feature.
          </p>

          <div className="ppsmi-unarchive-actions">
            <button
              className="ppsmi-unarchive-cancel-btn"
              onClick={() => setUnarchiveOpen(false)}
              type="button"
            >
              Cancel
            </button>

            <button
              className="ppsmi-unarchive-confirm-btn"
              onClick={() => handleStatusUpdate("unarchive")}
              type="button"
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PropertyPostSystemMenuIcon;
